import React, { EventHandler, useEffect, useState } from 'react'
import { serverError } from 'remix-utils'
import type { ZodError } from 'zod'
import { z } from 'zod'
import { zx } from 'zodix'

import type { ActionArgs, LoaderArgs, V2_MetaFunction } from '@remix-run/node'
import { json, redirect } from '@remix-run/node'
import {
  Form,
  Link,
  useActionData,
  useFetcher,
  useLoaderData,
  useSearchParams,
} from '@remix-run/react'

import Dropdown from '~/components/Dropdown'
import { createUserSession, getUserId } from '~/models/session.server'
import {
  checkUserExistsByEmail,
  createUser,
  signupUserInput,
} from '~/models/user.server'

import categoriesList from '../static/categories.json'

export const meta: V2_MetaFunction = () => [{ title: 'Sign Up' }]

export async function loader({ request }: LoaderArgs) {
  try {
  const { category } = zx.parseQuery(request, {
    category: z.string().optional(),
  })
  const userId = await getUserId(request)
  if (userId) return redirect('/')

  const categories = categoriesList.map((c) => c.Name)
  const subcategories = category
    ? categoriesList
      .find((c) => c.Name === category)
      ?.Children.map((c) => c.Name)
    : categoriesList[0].Children.map((c) => c.Name)

  return json({ categories, subcategories })
} catch (err) {
  return json({ categories:[], subcategories:[] })
}
}

export async function action({ request }: ActionArgs) {
  try {
    const userInput = await zx.parseFormSafe(request, signupUserInput)
    if (!userInput.success) {
      return json({ errors: userInput.error.issues }, { status: 400 })
    }

    const { email, redirectTo } = userInput.data

    const existingUser = await checkUserExistsByEmail(email)
    if (existingUser) {
      return json(
        {
          errors: [
            {
              message: 'User already exists, perhaps try to log in?',
              code: 'custom',
              path: ['existingUser'],
            },
          ] as ZodError['issues'],
        },
        { status: 400 }
      )
    }
    const user = await createUser(userInput.data)

    if (!user) {
      throw serverError('Unable to create new user, please try again')
    }
    return createUserSession({
      request,
      user,
      remember: false,
      redirectTo,
    })
  } catch (err: any) {
    console.log('\n singup action error:', err.message || err);
    let message = err.message || 'Something went wrong'
    return json(
      {
        errors: [
          {
            message: message,
            code: 'custom',
          },
        ] as ZodError['issues'],
      },
      { status: 400 }
    )
  }
}

export default function Join() {
  const { categories, subcategories } = useLoaderData<typeof loader>()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo') ?? '/campaigns'
  const actionData = useActionData<typeof action>()
  const emailRef = React.useRef<HTMLInputElement>(null)
  const passwordRef = React.useRef<HTMLInputElement>(null)
  const confirmPasswordRef = React.useRef<HTMLInputElement>(null)
  
  const existingUserError = actionData?.errors.find((e: any) =>
    e?.path?.includes('existingUser')
  )
  const emailError = actionData?.errors.find((e: any) => e?.path?.includes('email'))
  const passwordError = actionData?.errors.find((e: any) =>
    e?.path?.includes('password')
  )
  const confirmPasswordError = actionData?.errors.find((e: any) =>
    e?.path?.includes('confirmPassword')
  )

  useEffect(() => {
    if (emailError || existingUserError) {
      emailRef.current?.focus()
    } else if (passwordError) {
      passwordRef.current?.focus()
    } else if (confirmPasswordError) {
      confirmPasswordRef.current?.focus()
    }
  }, [existingUserError, emailError, passwordError, confirmPasswordError])

  const subcategoryFetcher = useFetcher()
  function fetchSubcategories(e: React.ChangeEvent<HTMLSelectElement>) {
    subcategoryFetcher.load(
      `/signup?category=${encodeURIComponent(e.target.value)}`
    )
  }

  useEffect(()=>{
    console.log('\n action data...', actionData)
  },[actionData])

  return (
    <main>
      <section className="mt-24 overflow-hidden bg-white xl:pb-56">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading tracking-px-n mb-[45px] text-center text-6xl font-bold leading-tight text-primary md:text-7xl">
              Sign Up
            </h2>
            <p className="mb-3">
            Before we can get started, we just need a few more details.
            </p>
            <Form method="post">
              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">Your Name</p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  id="name"
                  required
                  autoFocus={true}
                  name="name"
                  type="text"
                  placeholder="First &amp; Last Name"
                />
              </label>

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">Email</p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  ref={emailRef}
                  id="email"
                  required
                  name="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={
                    emailError || existingUserError ? true : undefined
                  }
                  aria-describedby="email-error"
                  placeholder="Email Address"
                />
                {emailError && (
                  <div className="pt-1 text-red-700" id="email-error">
                    {emailError.message}
                  </div>
                )}
                {existingUserError && (
                  <div className="pt-1 text-red-700" id="email-error">
                    {existingUserError.message}
                  </div>
                )}
              </label>

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">
                    Company Name
                  </p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="company"
                  type="text"
                  required
                  placeholder="Company Name"
                />
                <p className="m-1 text-xs text-gray-500">
                  This will be the name of your default advertiser
                </p>
              </label>
              {/* <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">Website</p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="websiteUrl"
                  type="url"
                  placeholder="Website Url"
                  required
                />
              </label>

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">Logo URL</p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="logoUrl"
                  type="text"
                  placeholder="Logo Url"
                  required
                />
                <p className="m-1 text-xs text-gray-500">
                  Link to a png or jpeg of your logo
                </p>
              </label>

              <Dropdown
                label="Category"
                name="category"
                options={categories}
                required={true}
                onChange={fetchSubcategories}
              />

              <Dropdown
                label="Subcategory"
                name="subcategory"
                options={
                  subcategoryFetcher.data?.subcategories ?? subcategories
                }
                required={true}
              /> */}

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">
                    Company Address
                  </p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="addressStreet"
                  type="text"
                  placeholder="Street"
                  required
                />
                <div className="flex  w-full space-x-2 pt-3.5">
                  <input
                    className=" w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700 md:w-2/3"
                    name="addressCity"
                    type="text"
                    placeholder="City"
                    required
                  />
                  <input
                    className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700 md:w-1/3"
                    name="addressPostcode"
                    type="text"
                    placeholder="Postcode"
                    required
                    maxLength={10}
                    minLength={4}
                  />
                </div>
              </label>

              <div className="flex w-full space-x-0 md:space-x-2 flex-wrap md:flex-nowrap">
                <label className="mb-5 block w-full text-left">
                  <div className="flex space-x-2">
                    <p className="pb-2 font-medium text-[#525252]">Password</p>
                    <p className="font-bold text-primary">*</p>
                  </div>
                  <input
                    className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                    id="password"
                    ref={passwordRef}
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    aria-invalid={passwordError ? true : undefined}
                    aria-describedby="password-error"
                    placeholder="Password"
                    required
                  />
                  {passwordError && (
                    <div className="pt-1 text-red-700" id="password-error">
                      {passwordError?.message}
                    </div>
                  )}
                </label>

                <label className="mb-5 block w-full text-left">
                  <div className="flex space-x-2">
                    <p className="pb-2 font-medium text-[#525252]">
                      Confirm Password
                    </p>
                    <p className="font-bold text-primary">*</p>
                  </div>
                  <input
                    className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                    id="confirmPassword"
                    ref={confirmPasswordRef}
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm Password"
                    required
                  />
                  {confirmPasswordError && (
                    <div className="pt-1 text-red-700" id="password-error">
                      {confirmPasswordError?.message}
                    </div>
                  )}
                </label>
              </div>
              <input type="hidden" name="redirectTo" value={redirectTo} />
              <button
                className="hover:shadow-4xl mb-8 w-full rounded-xl border bg-secondary py-4  px-9 font-semibold text-white shadow-sm transition duration-200 hover:bg-red-700 ease-in-out focus:ring focus:ring-indigo-300"
                type="submit"
              >
                Sign Up
              </button>
              {/* <p className="space-x-2 font-medium">
                <span>Already have an account?</span>
                <Link className="text-primary hover:text-green-700" to="/login">
                  Login
                </Link>
              </p> */}
            </Form>
          </div>
        </div>
      </section>
    </main>
  )
}
